// dangerous-command-guard-opt-in.ts
//
// team-ai-pack origin/main (7a29c58) made pretooluse-dangerous-command-guard-hook.py
// OPT-IN: `guard_enabled()` reads `.claude/state/dangerous-command-guard.on` and
// `exit 0`s BEFORE reading stdin when the flag is absent — silently disabling the
// autopilot deny-by-binding guard (P1, spec §21/F17) in a mission workspace unless
// this file exists. `.claude/state/` is already gitignored (mission-workspace.ts's
// MISSION_IGNORE_ENTRIES) and controller-owned (controller-owned-paths.ts's
// CONTROLLER_OWNED_PATH_GLOBS), so writing an empty marker here at init time is safe
// and needs no new allowlisting.
//
// Single source of truth for the path so mission-workspace.ts (writer) and
// autopilot-doctor.ts (reader/checker) can never drift on the literal.

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { ensureDir, pathExists } from "../filesystem-helpers.js";
import type { AutopilotCheckResult } from "./autopilot-doctor.js";

/** Relative to a mission's `.claude` dir. */
export const DANGEROUS_COMMAND_GUARD_OPT_IN_FLAG_RELATIVE_PATH = join(
  "state",
  "dangerous-command-guard.on",
);

/** Empty marker file — content is irrelevant to the hook, only presence/absence is read. */
export async function writeDangerousCommandGuardOptInFlag(claudeDir: string): Promise<void> {
  const flagPath = join(claudeDir, DANGEROUS_COMMAND_GUARD_OPT_IN_FLAG_RELATIVE_PATH);
  await ensureDir(join(claudeDir, "state"));
  await fs.writeFile(flagPath, "", "utf8");
}

/**
 * Doctor-facing check, split out of autopilot-doctor.ts (200-line convention
 * ceiling) — same seam as autopilot-doctor-mattermost-check.ts. Mission
 * workspaces created by mission-workspace.ts always write this flag; its
 * absence here means either a pre-fix workspace or the flag got removed
 * after init — warn (not block `run`) and point at recreating the mission
 * or `doctor --reverify`.
 */
export async function checkDangerousCommandGuardOptInForDoctor(
  missionWorkspace: string,
): Promise<AutopilotCheckResult> {
  const flagPath = join(
    missionWorkspace,
    ".claude",
    DANGEROUS_COMMAND_GUARD_OPT_IN_FLAG_RELATIVE_PATH,
  );
  if (await pathExists(flagPath)) {
    return {
      name: "Dangerous-command-guard opt-in",
      status: "ok",
      detail: `flag có mặt tại ${flagPath} — guard đang bật trong mission.`,
    };
  }
  return {
    name: "Dangerous-command-guard opt-in",
    status: "warn",
    detail:
      `Thiếu ${flagPath} — pretooluse-dangerous-command-guard-hook.py (team-ai-pack) sẽ ` +
      "exit 0 TRƯỚC khi đọc input, tắt guard deny-by-binding trong mission này. " +
      "Chạy 'avatar autopilot doctor --reverify' hoặc dựng lại mission workspace.",
  };
}
